import React from 'react';
import { motion } from 'framer-motion';
import { FaLinkedin, FaEnvelope, FaHeart, FaGlobe } from 'react-icons/fa';

const Footer = () => {
  return (
    <footer className="glass-strong border-t border-white/10 py-8 sm:py-10 md:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-6 sm:mb-8">
          {/* Brand */}
          <div>
            <motion.div
              className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4"
              whileHover={{ scale: 1.05 }}
            >
              <div className="relative w-10 h-10 sm:w-12 sm:h-12">
                <img 
                  src="/sdc-logo.webp" 
                  alt="SDC Logo" 
                  className="w-full h-full object-contain"
                />
                <div className="absolute inset-0 blur-xl bg-primary/30"></div>
              </div>
              <div>
                <span className="text-xl sm:text-2xl font-bold gradient-text">SDC</span>
                <p className="text-[10px] sm:text-xs text-gray-400 font-mono">Software Development Club</p>
              </div>
            </motion.div>
            <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
              Building the future through code, collaboration, and innovation. Join us in shaping tomorrow's technology.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-3 sm:mb-4 text-white text-sm sm:text-base">Quick Links</h4>
            <ul className="space-y-1.5 sm:space-y-2">
              <FooterLink href="#home">Home</FooterLink>
              <FooterLink href="#about">About Us</FooterLink>
              <FooterLink href="#projects">Projects</FooterLink>
              <FooterLink href="#team">Team</FooterLink>
              <FooterLink href="#contact">Contact</FooterLink>
            </ul>
          </div>

          {/* Contact & Social */}
          <div>
            <h4 className="font-semibold mb-3 sm:mb-4 text-white text-sm sm:text-base">Connect With Us</h4>
            <div className="space-y-2 sm:space-y-3 mb-3 sm:mb-4">
              <a 
                href="mailto:sdc@mitsgwalior.in" 
                className="flex items-center space-x-2 text-gray-400 hover:text-primary transition-colors group"
              >
                <FaEnvelope className="group-hover:scale-110 transition-transform text-sm sm:text-base" />
                <span className="text-xs sm:text-sm break-all">sdc@mitsgwalior.in</span>
              </a>
            </div>
            <div className="flex space-x-3 sm:space-x-4">
              <SocialLink href="https://www.linkedin.com/company/software-development-club-mits/" icon={<FaLinkedin />} label="LinkedIn" />
              <SocialLink href="https://web.mitsgwalior.in/" icon={<FaGlobe />} label="MITS Website" />
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 sm:pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-3 sm:space-y-4 md:space-y-0">
            <p className="text-gray-400 text-xs sm:text-sm flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
              <span>© 2025 Software Development Club</span>
              <span className="hidden sm:inline">•</span>
              <span>All rights reserved</span>
            </p>
            
            <motion.div 
              className="flex flex-wrap items-center justify-center space-x-1.5 sm:space-x-2 text-xs sm:text-sm"
              whileHover={{ scale: 1.02 }}
            >
              <span className="text-gray-400">Crafted with</span>
              <FaHeart className="text-red-500 animate-pulse mx-0.5 sm:mx-1" />
              <span className="text-gray-400">by</span>
              <a 
                href="https://www.linkedin.com/in/ayan-ahmed-khan-95978620a/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="relative group"
              >
                <span className="font-bold bg-gradient-to-r from-primary via-accent to-emerald bg-clip-text text-transparent group-hover:from-accent group-hover:via-emerald group-hover:to-primary transition-all duration-300">
                  Ayan Ahmed Khan
                </span>
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-emerald group-hover:w-full transition-all duration-300"></span>
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </footer>
  );
};

const FooterLink = ({ href, children }) => (
  <li>
    <a 
      href={href}
      className="text-gray-400 hover:text-primary transition-colors text-sm flex items-center group"
    >
      <span className="mr-2 text-primary opacity-0 group-hover:opacity-100 transition-opacity">→</span>
      {children}
    </a>
  </li>
);

const SocialLink = ({ href, icon, label }) => (
  <motion.a
    href={href}
    aria-label={label}
    className="w-10 h-10 rounded-full glass flex items-center justify-center text-gray-400 hover:text-primary hover:bg-primary/10 transition-all"
    whileHover={{ scale: 1.15, rotate: 5 }}
    whileTap={{ scale: 0.95 }}
  >
    {icon}
  </motion.a>
);

export default Footer;
